/**
 * Utilities for converting markdown to DOCX using @m2d/core
 * Works in both browser and server environments. Client-only plugins
 * (mermaid, image) are enabled only when running in the browser.
 *
 * Usage Example (client download):
 *
 * ```typescript
 * import { convertAndDownloadMarkdownToDocx } from '@/lib/utils/markdown-to-docx';
 *
 * // In a React component or event handler
 * await convertAndDownloadMarkdownToDocx(
 *   markdownContent,
 *   'my-document.docx',
 *   {
 *     mermaidSupport: true,
 *     theme: 'default',
 *     fontFamily: 'Arial'
 *   }
 * );
 * ```
 */

import { downloadBlob } from './blob-download';
import { normalizeMarkdownBullets } from './markdown';
import { drawioMermaidLink } from './drawio';

export interface MarkdownToDocxOptions {
  mermaidSupport?: boolean;
  theme?: string;
  fontFamily?: string;
}

/**
 * Convert markdown string to DOCX blob using @m2d/core with full plugin support
 * This function must be called from browser environment where DOM is available
 *
 * @param markdown - The markdown content to convert
 * @param options - Conversion options
 * @returns Promise<Blob> - The converted DOCX file as a blob
 */
export async function convertMarkdownToDocx(
  markdown: string,
  options: MarkdownToDocxOptions = {},
): Promise<Blob> {
  try {
    let preparedMarkdown = normalizeMarkdownBullets(markdown);
    preparedMarkdown = appendDrawioLinksForMermaid(preparedMarkdown);

    // Dynamic imports for @m2d packages
    const { unified } = await import('unified');
    const { default: remarkParse } = await import('remark-parse');
    const { default: remarkGfm } = await import('remark-gfm');
    const { default: remarkFrontmatter } = await import('remark-frontmatter');
    const { default: remarkMath } = await import('remark-math');
    const { toDocx } = await import('@m2d/core');
    const { listPlugin } = await import('@m2d/list');
    const { htmlPlugin } = await import('@m2d/html');
    const { tablePlugin } = await import('@m2d/table');

    // Create unified processor with remark plugins
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkFrontmatter)
      .use(remarkMath);

    // Base plugins safe for both environments
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const plugins: any[] = [htmlPlugin(), tablePlugin(), listPlugin()];

    // Client-only plugins: image and mermaid (guarded by window)
    const isClient = typeof window !== 'undefined';
    if (isClient) {
      const { imagePlugin } = await import('@m2d/image');
      plugins.push(imagePlugin());
      if (options.mermaidSupport) {
        const { mermaidPlugin } = await import('@m2d/mermaid');
        const repairFallback = (code: string) => code.replace(/;/g, ' --> ');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mermaidPluginOptions: Record<string, any> = {
          mermaidConfig: {
            // theme: options.theme || 'default',
            fontFamily: options.fontFamily || 'Arial',
          },
          cacheConfig: {
            cacheMode: 'memory',
          },
          fixMermaid: async (code: string, error?: unknown) => {
            const errorMessage =
              typeof error === 'string'
                ? error
                : error instanceof Error
                  ? error.message
                  : typeof (error as Record<string, unknown>)?.message === 'string'
                    ? ((error as Record<string, unknown>).message as string)
                    : undefined;

            if (errorMessage) {
              console.warn('Mermaid syntax error detected, attempting repair via API', errorMessage);
            } else if (error) {
              console.warn('Mermaid syntax error detected, attempting repair via API', error);
            }

            try {
              const response = await fetch('/api/mermaid/repair', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  diagram: code,
                  error: errorMessage,
                }),
              });

              if (response.ok) {
                const payload = await response.json();
                if (typeof payload?.diagram === 'string' && payload.diagram.trim().length > 0) {
                  return payload.diagram as string;
                }

                if (Array.isArray(payload?.validationErrors) && payload.validationErrors.length > 0) {
                  console.warn('Mermaid repair validation errors:', payload.validationErrors);
                }
              } else {
                const errorPayload = await response.json().catch(() => ({}));
                console.warn(
                  'Mermaid repair API returned non-OK response',
                  response.status,
                  errorPayload,
                );
              }
            } catch (apiError) {
              console.error('Mermaid repair request failed', apiError);
            }

            return repairFallback(code);
          },
        };

        plugins.unshift(mermaidPlugin(mermaidPluginOptions));
      }
    }

    // Parse markdown to MDAST and apply plugins
    const mdast = processor.parse(preparedMarkdown);

    // Convert MDAST to DOCX
    const docxBuffer = await toDocx(mdast, {}, { plugins });

    // Return blob directly if already a blob
    if (docxBuffer instanceof Blob) {
      return docxBuffer;
    }

    // Convert other types to blob
    let buffer: ArrayBuffer | Uint8Array;
    if (docxBuffer instanceof ArrayBuffer) {
      buffer = docxBuffer;
    } else if (docxBuffer instanceof Uint8Array) {
      buffer = docxBuffer;
    } else if (Array.isArray(docxBuffer)) {
      buffer = new Uint8Array(docxBuffer as number[]);
    } else if (typeof docxBuffer === 'string') {
      buffer = new TextEncoder().encode(docxBuffer);
    } else {
      throw new Error(`Unsupported buffer type: ${typeof docxBuffer}`);
    }
    // @ts-expect-error - Blob type compatibility with Node.js buffer
    return new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  } catch (error) {
    console.error('Markdown to DOCX conversion error:', error);
    throw new Error(
      `Failed to convert markdown to DOCX: ${error instanceof Error ? error.message : error}`,
    );
  }
}

/**
 * Download a DOCX blob as a file
 *
 * @param blob - The DOCX blob to download
 * @param filename - The filename for the download
 */
export function downloadDocxBlob(blob: Blob, filename: string): void {
  const normalized = filename.endsWith('.docx') ? filename : `${filename}.docx`;
  downloadBlob(blob, normalized);
}

/**
 * Convert markdown to DOCX and trigger download
 *
 * @param markdown - The markdown content to convert
 * @param filename - The filename for the download
 * @param options - Conversion options
 */
export async function convertAndDownloadMarkdownToDocx(
  markdown: string,
  filename: string,
  options: MarkdownToDocxOptions = {},
): Promise<void> {
  const blob = await convertMarkdownToDocx(markdown, options);
  downloadDocxBlob(blob, filename);
}

/**
 * Append draw.io links for any mermaid diagrams found in the markdown payload.
 */
function appendDrawioLinksForMermaid(markdown: string): string {
  if (!markdown) {
    return markdown;
  }

  const mermaidRegex = /```mermaid\s*([\s\S]*?)```/gi;
  let diagramIndex = 0;

  return markdown.replace(mermaidRegex, (match, diagramContent, offset, full) => {
    diagramIndex += 1;

    const diagram = typeof diagramContent === 'string' ? diagramContent.trim() : '';
    if (!diagram) {
      return match;
    }

    const afterBlock = full.slice(offset + match.length);
    if (/^\s*(?:-\s+)?\[Edit\s+(?:this\s+)?diagram(?:\s+\d+)?\s+in draw\.io\]/i.test(afterBlock)) {
      return match;
    }

    const link = drawioMermaidLink(diagram, { title: `BA-Assist Diagram ${diagramIndex}` });
    const linkLine = `<a href="${link}" style="color:#2563eb;text-decoration:underline;">Edit diagram ${diagramIndex} in draw.io</a>`;
    const separator = endsWithNewline(match) ? '' : '\n';

    return `${match}${separator}${linkLine}`;
  });
}

function endsWithNewline(value: string): boolean {
  return /\n$/.test(value);
}
